import {
  subscribeSaveFailed,
  subscribeSavePersisted,
  type SaveFailedEvent,
  type SavePersistedEvent,
} from "./saveEvents";

export interface SaveBarrierMetadata {
  saveAttemptId?: string | null;
  originMutationId?: string | null;
  reason?: string | null;
}

type SaveBarrierRecord = {
  key: string;
  metadata: SaveBarrierMetadata;
  openedAt: number;
};

const activeSaveBarriers = new Map<string, SaveBarrierRecord>();

const buildBarrierKeys = ({
  saveAttemptId,
  originMutationId,
}: SaveBarrierMetadata): string[] => {
  const keys: string[] = [];
  if (typeof saveAttemptId === "string" && saveAttemptId.trim()) {
    keys.push(`attempt:${saveAttemptId.trim()}`);
  }
  if (typeof originMutationId === "string" && originMutationId.trim()) {
    keys.push(`origin:${originMutationId.trim()}`);
  }
  return keys;
};

export const openSaveBarrier = (metadata: SaveBarrierMetadata): void => {
  for (const key of buildBarrierKeys(metadata)) {
    activeSaveBarriers.set(key, {
      key,
      metadata,
      openedAt: Date.now(),
    });
  }
};

export const resolveSaveBarrier = (metadata: SaveBarrierMetadata): void => {
  for (const key of buildBarrierKeys(metadata)) {
    activeSaveBarriers.delete(key);
  }
};

export const hasOpenSaveBarriers = (): boolean => activeSaveBarriers.size > 0;

/**
 * 특정 save attempt의 저장이 아직 진행 중인지.
 *
 * `openSaveBarrier`는 persist 요청 시점에 열리고 persisted·failed 어느 쪽이든
 * `resolveSaveBarrier`로 닫힌다. 따라서 이 값이 true인 동안은 그 저장 job이
 * 살아 있다고 본다.
 */
export const hasOpenSaveBarrierForAttempt = (
  saveAttemptId: string | null | undefined,
): boolean => {
  const normalized =
    typeof saveAttemptId === "string" ? saveAttemptId.trim() : "";
  if (!normalized) {
    return false;
  }
  return activeSaveBarriers.has(`attempt:${normalized}`);
};

export const clearSaveBarriersForTests = (): void => {
  activeSaveBarriers.clear();
};

export const waitForSaveBarrierResolution = ({
  saveAttemptId,
  originMutationId,
  timeoutMs = 30000,
}: SaveBarrierMetadata & { timeoutMs?: number }): Promise<void> => {
  const keys = buildBarrierKeys({ saveAttemptId, originMutationId });
  if (keys.length === 0) {
    return Promise.resolve();
  }

  const isResolved = () => keys.every((key) => !activeSaveBarriers.has(key));
  if (isResolved()) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error("save barrier resolution timed out"));
    }, timeoutMs);

    const onResolved = (event: SavePersistedEvent | SaveFailedEvent) => {
      const detail = event.detail;
      const detailKeys = buildBarrierKeys(detail ?? {});
      if (detailKeys.length === 0) return;
      if (!detailKeys.some((key) => keys.includes(key))) return;
      if (!isResolved()) return;
      cleanup();
      resolve();
    };

    const cleanup = () => {
      clearTimeout(timeoutId);
      unsubscribePersisted();
      unsubscribeFailed();
    };

    const unsubscribePersisted = subscribeSavePersisted(onResolved);
    const unsubscribeFailed = subscribeSaveFailed(onResolved);
  });
};

export const waitForOpenSaveBarriersToDrain = ({
  timeoutMs = 20000,
}: {
  timeoutMs?: number;
} = {}): Promise<void> => {
  if (!hasOpenSaveBarriers()) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    const startedAt = Date.now();
    const timeoutId = window.setTimeout(() => {
      cleanup();
      resolve();
    }, timeoutMs);

    const onChanged = () => {
      if (!hasOpenSaveBarriers()) {
        cleanup();
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        cleanup();
        resolve();
      }
    };

    const cleanup = () => {
      clearTimeout(timeoutId);
      unsubscribePersisted();
      unsubscribeFailed();
    };

    const unsubscribePersisted = subscribeSavePersisted(onChanged);
    const unsubscribeFailed = subscribeSaveFailed(onChanged);
  });
};
